<script lang="ts">
	/**
	 * The ApexCharts wrapper, and the only place in the app that touches the library.
	 *
	 * It is a wrapper rather than flowbite's own `Chart` for one reason: **it cannot be allowed to
	 * throw.** Flowbite's version calls an unawaited async `render()` inside a `use:` action, so a
	 * failure there becomes an unhandled rejection — and ApexCharts measures SVG text, which jsdom
	 * does not implement, so under `vitest` that is not an edge case but the default. A dashboard
	 * whose tests cannot render its own pages is a dashboard nothing else about it can be asserted
	 * on, and an admin screen that white-screens because one panel's library failed is worse than
	 * the panel being missing.
	 *
	 * So: the import is dynamic, every call is guarded, and a chart that will not draw leaves the
	 * `fallback` slot on screen. Every panel that uses this also renders the numbers as text or as
	 * a table beside it, which is the same rule stated from the other end — the chart is the
	 * fastest way to read the data, never the only way.
	 */

	import { onDestroy, onMount } from 'svelte';

	/** ApexCharts options, already merged with `baseChartOptions`. */
	export let options: Record<string, unknown> = {};
	/** Announced to a screen reader in place of the SVG, which is decorative to one. */
	export let label: string = 'Chart';

	interface ApexInstance {
		/** Returns a promise, and **rejects** on a surface it cannot measure. See `fnCreate`. */
		render: () => Promise<unknown>;
		updateOptions: (options: unknown) => Promise<unknown>;
		destroy: () => void;
	}

	let element: HTMLDivElement | null = null;
	let chart: ApexInstance | null = null;
	let failed = false;
	let mounted = false;
	let destroyed = false;

	/**
	 * Build the chart, and **await the render**.
	 *
	 * `render()` is asynchronous and rejects, so a `try` around an un-awaited call catches nothing:
	 * the rejection lands after the function has returned, as an unhandled rejection that takes the
	 * test run with it and leaves a live page with a silent hole. That is not a corner case — it is
	 * the default under `vitest`, where jsdom implements none of the SVG text measurement ApexCharts
	 * needs, and it is also what a page destroyed mid-render produces in a browser.
	 */
	async function fnCreate(): Promise<void> {
		if (element === null) return;
		try {
			const ApexCharts = (await import('apexcharts')).default;
			if (destroyed) return;
			const instance = new ApexCharts(element, options) as unknown as ApexInstance;
			await instance.render();
			if (destroyed) {
				instance.destroy();
				return;
			}
			chart = instance;
			failed = false;
		} catch {
			// jsdom, a blocked chunk, or a malformed option set. The fallback stays on screen and
			// the numbers are in the table beside it.
			failed = true;
			chart = null;
		}
	}

	onMount(() => {
		mounted = true;
		void fnCreate();
	});

	onDestroy(() => {
		destroyed = true;
		try {
			chart?.destroy();
		} catch {
			/* Destroying a chart that never rendered is not a failure worth reporting. */
		}
		chart = null;
	});

	/*
	 * Reactive on `options` alone. `updateOptions` rather than a rebuild, because a rebuild on every
	 * filter change discards the canvas and flashes — and `animations: false` in the base options is
	 * the other half of making a filter change read as a comparison rather than as a new chart.
	 */
	$: if (mounted && chart !== null && options) {
		// `.catch` as well as `try`, for the reason `fnCreate` awaits: this returns a promise too.
		try {
			void chart.updateOptions(options).catch(() => {
				failed = true;
			});
		} catch {
			failed = true;
		}
	}
</script>

<div class="w-full" role="img" aria-label={label}>
	<div bind:this={element} class:hidden={failed}></div>
	{#if failed}
		<slot name="fallback">
			<p class="text-default text-sm py-4">
				This chart could not be drawn. The figures it is drawn from are listed below it.
			</p>
		</slot>
	{/if}
</div>
